import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { downloadReport, generateReport, listReports } from "@/api/endpoints/reports";
import type { ReportJob } from "@/api/endpoints/reports";

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "done" ? "bg-success/10 text-success" : status === "failed" ? "bg-critical/10 text-critical" : "bg-warning/10 text-warning";
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${tone}`}>{status}</span>;
}

export function ReportsPage() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState("daily");
  const [format, setFormat] = useState("pdf");

  const reportsQuery = useQuery({ queryKey: ["reports"], queryFn: listReports });

  const generateMutation = useMutation({
    mutationFn: () => generateReport(period, format),
    onSuccess: () => {
      toast.success("Report generated");
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border border-border bg-surface p-5">
        <h2 className="mb-1 text-lg font-semibold text-text">Generate a report</h2>
        <p className="mb-4 text-sm text-text-muted">
          On-demand for now — daily covers the last 24h, weekly the last 7 days, monthly the last
          30 days, pulled live from radacct/radpostauth.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-text outline-none focus:border-accent"
          >
            <option value="pdf">PDF</option>
            <option value="xlsx">Excel</option>
          </select>
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-bg disabled:opacity-60"
          >
            {generateMutation.isPending ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-text-muted">
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Period</th>
              <th className="px-4 py-3">Format</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Download</th>
            </tr>
          </thead>
          <tbody>
            {reportsQuery.data?.items.map((job: ReportJob) => (
              <tr key={job.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 text-text-muted">{new Date(job.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 capitalize text-text">{job.period}</td>
                <td className="px-4 py-3 uppercase text-text-muted">{job.format}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={job.status} />
                  {job.error && <p className="mt-1 max-w-xs text-xs text-critical">{job.error}</p>}
                </td>
                <td className="px-4 py-3">
                  {job.download_url ? (
                    <button
                      onClick={() => downloadReport(job).catch((e) => toast.error(e.message))}
                      className="text-accent hover:underline"
                    >
                      Download
                    </button>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
            {reportsQuery.data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-text-muted">
                  No reports generated yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
